/**
 * Bridge between the editor's HTML and the stored document for raw-mode (`{{...}}`) tokens.
 *
 * TipTap can only serialize a node to an element, not to bare text, so a raw-mode merge field
 * round-trips through the editor as a `<span data-merge-symbol="{{...}}">label</span>` marker
 * (see mergeFieldToken.ts). These helpers translate that marker to/from the literal `{{...}}`
 * text that the backend resolves at send-time — keeping the saved document byte-compatible with
 * documents authored by the legacy editor. Span-mode (`data-placeholder`) tokens are left alone.
 */

export interface IMergeFieldToken {
	/** The literal token as it appears in the stored document, e.g. '{{meeting day}}'. */
	symbol: string;
	/** Friendly label shown inside the editor pill, e.g. 'Meeting Day'. */
	text: string;
	/**
	 * Present for span-mode (contact/generic) tokens, e.g. 'contact.firstName': on load these become
	 * a `data-placeholder` span (and serialize back to one). Absent for raw-mode tokens, which
	 * round-trip as bare `{{...}}` text.
	 */
	value?: string;
}

const MARKER_SELECTOR = 'span[data-merge-symbol]';

function escapeRegExp(value: string): string {
	return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Editor HTML -> stored document: unwrap each marker span to its literal `{{...}}` token. */
export function serializeMergeFieldTokens(editorHtml: string): string {
	const doc = new DOMParser().parseFromString(editorHtml, 'text/html');
	doc.body.querySelectorAll(MARKER_SELECTOR).forEach(el => {
		el.replaceWith(doc.createTextNode(el.getAttribute('data-merge-symbol') ?? ''));
	});
	return doc.body.innerHTML;
}

/**
 * Stored document -> editor HTML: wrap each known raw `{{...}}` token (matched verbatim, including
 * aliases in `tokens`) in a marker span so the editor renders it as a pill. Unknown tokens and
 * legacy `data-placeholder` spans are left untouched.
 */
export function deserializeMergeFieldTokens(storedHtml: string, tokens: ReadonlyArray<IMergeFieldToken>): string {
	if (tokens.length === 0) {
		return storedHtml;
	}
	const tokenBySymbol = new Map(tokens.map(t => [t.symbol, t]));
	// Longest-first so a token that is a prefix of another can't win the shorter match.
	const pattern = tokens
		.map(t => t.symbol)
		.sort((a, b) => b.length - a.length)
		.map(escapeRegExp)
		.join('|');
	const splitter = new RegExp(`(${pattern})`, 'g');

	const doc = new DOMParser().parseFromString(storedHtml, 'text/html');
	const textNodes: Text[] = [];
	const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
	for (let node = walker.nextNode(); node; node = walker.nextNode()) {
		textNodes.push(node as Text);
	}

	for (const textNode of textNodes) {
		const value = textNode.nodeValue ?? '';
		// split with a capture group keeps the matched tokens as their own array entries;
		// a single-element result means no token matched, so leave the node alone.
		const pieces = value.split(splitter);
		if (pieces.length === 1) {
			continue;
		}
		const fragment = doc.createDocumentFragment();
		for (const piece of pieces) {
			if (piece === '') {
				continue;
			}
			const token = tokenBySymbol.get(piece);
			if (!token) {
				fragment.appendChild(doc.createTextNode(piece));
				continue;
			}
			const span = doc.createElement('span');
			if (token.value) {
				// Span-mode (contact/generic): the legacy placeholder span the node + backend expect.
				span.setAttribute('contenteditable', 'false');
				span.setAttribute('data-placeholder', 'true');
				span.setAttribute('data-placeholder-value', token.value);
			} else {
				// Raw-mode: marker that serializes back to the bare {{...}} token.
				span.setAttribute('data-merge-symbol', piece);
			}
			span.textContent = token.text;
			fragment.appendChild(span);
		}
		textNode.replaceWith(fragment);
	}
	return doc.body.innerHTML;
}
